/**
 * Pure helpers for the Connection Health cadence control. The proxy clamps the
 * effective cadence to a 5s floor on the runner side; we surface presets from
 * that floor upward and format them for the Select. Kept framework-free so the
 * formatting + option-building can be unit-tested directly.
 */

/** Runner-side minimum effective cadence (proxy clamps to this). */
export const MIN_CADENCE_MS = 5000

/** Default org cadence when no row exists yet (matches the proxy default). */
export const DEFAULT_CADENCE_MS = 60000

export interface CadenceOption {
  value: number
  label: string
}

const PRESETS_MS = [5000, 15000, 30000, 60000, 120000, 300000, 600000, 900000, 1800000, 3600000]

/**
 * Format a cadence in ms as a friendly duration ("5 seconds", "1 minute",
 * "2 minutes", "1 hour"). Non-round values fall back to whole seconds, and
 * sub-second / invalid values render an em dash.
 */
export function formatCadence(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  const units: Array<[number, string]> = [
    [3600000, 'hour'],
    [60000, 'minute'],
    [1000, 'second']
  ]
  for (const [unitMs, name] of units) {
    if (ms % unitMs === 0) {
      const n = ms / unitMs
      return `${n} ${name}${n === 1 ? '' : 's'}`
    }
  }
  return `${ms} ms`
}

/**
 * Build the Select options: the presets (connection-health presets unless the
 * caller supplies its own) plus the current value if it is
 * a valid cadence that isn't already a preset (so an API-set custom cadence is
 * still selectable without silently snapping to the nearest preset). Sorted
 * ascending.
 */
export function buildCadenceOptions(
  currentMs?: number,
  presetsMs: readonly number[] = PRESETS_MS
): CadenceOption[] {
  const values = new Set(presetsMs)
  if (typeof currentMs === 'number' && Number.isFinite(currentMs) && currentMs > 0) {
    values.add(currentMs)
  }
  return [...values].sort((a, b) => a - b).map((value) => ({ value, label: formatCadence(value) }))
}
